import React, { useContext, useState, useCallback } from 'react';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import Button from 'ls-common-client/dist/components/Button';
import AlertDialog from 'ls-common-client/dist/components/AlertDialog';
import Text from 'ls-common-client/dist/components/Text';
import Image from 'ls-common-client/dist/components/Image';
import EmptyButton from 'ls-common-client/dist/components/EmptyButton';
import Icon from 'ls-common-client/dist/components/Icon';
import TextReveal from 'ls-common-client/dist/components/TextReveal';
import { resize } from '../../services/imageResizer';
import LazyLoad from '../../components/LazyLoad';
import ImageLightbox from '../../components/Lightbox/ImageLightbox';
import ReviewStars from '../../components/Review/ReviewStars';
import { Context } from '../../context/AppContext';
import ReviewUpdate from './ReviewUpdate';
import { deleteReviewQuery } from '../../gql/reviews';
import NextLink from '../../components/NextLink';

const StyledRoot = styled(Container)`
  border-bottom: solid 1px #ececec;
  margin-bottom: 20px;
  padding: 0 0 15px;
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 10px;
  }
`;

const StyledImageContainer = styled(Container)`
  margin: 25px 0 10px;
`;

const StyledImageList = styled(Container)`
  margin-bottom: 0;
  display: flex;
  flex-wrap: wrap;
`;

const StyledImage = styled(Container)`
  overflow: hidden;
  border-radius: 11px;
  width: 80px;
  height: 80px;
  margin-right: 10px;
  margin-bottom: 10px;
  @media (max-width: 1024px) and (orientation: portrait), (max-width: 767px) {
    width: 80px;
    height: 80px;
  }
  @media (max-width: 767px) {
    width: 80px;
    height: 80px;
  }
`;

const StyledReviewState = styled(Text)`
  display: inline-block;
  height: 25px;
  margin: 5px 0;
  padding: 3px 20px;
  border-radius: 12.5px;
  background-color: #ececec;
  color: ${props => props.theme.text.text300};
  font-size: 14px;
  margin-bottom: 10px;
`;

const StyledButton = styled(Button)`
  min-width: 90px;
  width: auto;
  height: 32px;
  font-size: 14px;
  font-weight: normal;
  display: flex;
  align-items: center;
  justify-content: center;
  i {
    height: 22px;
    font-size: 23px;
  }
`;

const StyledEditButton = styled(StyledButton)`
  border: solid 1px var(--color-lsBlue);
  margin-right: 8px;
  color: var(--color-lsBlue);
`;

const StyledDeleteButton = styled(StyledButton)`
  border: solid 1px #f16159;
  color: #f16159;
`;

const StyledBusinessName = styled(Text)`
  display: block;
`;

const StyledLink = styled(NextLink)`
  cursor: pointer;
  &:hover {
    & ${StyledBusinessName} {
      color: ${props => props.theme.primary.normal};
    }
  }
`;

const Review = ({ review, updateReviewCache, onDeleteSuccess }) => {
  const {
    media: { mobile },
    webp,
  } = useContext(Context);

  const {
    id,
    listing,
    createdAt,
    rating,
    message,
    reviewResponse,
    reviewState,
    displayName,
    reviewAssets,
  } = review || {};
  const { name, logo, vanityUrlPath } = listing || {};
  const { edges: assets = [] } = reviewAssets || {};
  const { message: responseMessage } = reviewResponse || {};

  const [showDialog, setShowDialog] = useState();
  const [showConfirmDelete, setShowConfirmDelete] = useState();

  const [mutate] = useMutation(deleteReviewQuery, {
    onCompleted: () => onDeleteSuccess(id),
  });

  const deleteReview = useCallback(() => {
    mutate({ variables: { id } });
  }, [id]);

  return (
    <>
      <StyledRoot>
        {reviewState && reviewState === 'pending' && (
          <StyledReviewState>Pending approval</StyledReviewState>
        )}
        {reviewState && reviewState === 'notApproved' && (
          <StyledReviewState>Not approved</StyledReviewState>
        )}
        <Container
          display="flex"
          justifyContent="space-between"
          marginBottom="15px"
        >
          <StyledLink to={`/profile/${vanityUrlPath}`}>
            <Container display="flex">
              <Container>
                {logo && (
                  <Container
                    src={resize(logo, {
                      width: 80,
                      height: 80,
                      webp,
                    })}
                    boxShadow="0 0 3px 0 rgba(0, 0, 0, 0.3)"
                    width="38px"
                    height="38px"
                    marginRight="10px"
                    borderRadius="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    <Image
                      objectFit="contain"
                      width="100%"
                      src={resize(logo, {
                        width: 80,
                        height: 80,
                        webp,
                      })}
                      alt="Business Logo"
                    />
                  </Container>
                )}
              </Container>
              <Container>
                {!mobile && (
                  <Text
                    fontSize="14px"
                    color="text400"
                    display="block"
                    lineHeight="1.1"
                    marginBottom="5px"
                  >
                    Your review for
                  </Text>
                )}
                <StyledBusinessName
                  fontSize="18px"
                  fontWeight="bold"
                  lineHeight="1.1"
                  color="normal"
                  wordBreak="break-word"
                >
                  {name}
                </StyledBusinessName>
                {mobile && (
                  <Container marginTop="2px">
                    <ReviewStars rating={rating} width={16} />
                  </Container>
                )}
              </Container>
            </Container>
          </StyledLink>
          <Container
            display="flex"
            alignItems="flex-end"
            justifyContent="flex-start"
            flexDirection="column"
            marginLeft="10px"
          >
            <Text
              fontSize={mobile ? '14px' : '16px'}
              color="text400"
              marginBottom="5px"
              whiteSpace="nowrap"
            >
              {moment(createdAt).fromNow()}
            </Text>
            {!mobile && (
              <Container marginRight="-12px">
                <ReviewStars rating={rating} width={20} />
              </Container>
            )}
          </Container>
        </Container>
        {message && (
          <Container marginBottom="15px">
            <TextReveal
              lines={3}
              wordBreak="break-word"
              display="block"
              fontSize="16px"
              renderShowMore={({ onClick, show }) => (
                <EmptyButton
                  onClick={onClick}
                  display="flex"
                  alignItems="center"
                  marginTop="10px"
                  color="text300"
                  fontSize="16px"
                >
                  See {show ? 'Less' : 'More'}
                  <Icon
                    iconSize="25px"
                    marginBottom="-7px"
                    className={`ls-icon icon-generalcaret${
                      show ? 'up' : 'down'
                    }medium`}
                  />
                </EmptyButton>
              )}
            >
              {message}
            </TextReveal>
          </Container>
        )}
        {assets && !!assets.length && (
          <StyledImageContainer>
            <StyledImageList>
              <ImageLightbox
                name={`reviewImage${id}`}
                images={assets.map(({ node: image }, key) => {
                  return {
                    ...image,
                    sub: `${message ? `${message} -` : '-'} ${displayName}`,
                    children: (
                      <StyledImage key={key}>
                        <LazyLoad>
                          <Image
                            src={resize(image, {
                              width: '80',
                              height: '80',
                              fit: 'cover',
                              webp,
                            })}
                            alt=""
                          />
                        </LazyLoad>
                      </StyledImage>
                    ),
                  };
                })}
              />
            </StyledImageList>
            <Text color="text300">
              {assets.length} photo{assets.length > 1 ? 's' : ''}
            </Text>
          </StyledImageContainer>
        )}
        <Container display="flex" alignItems="center">
          <StyledEditButton
            type="button"
            rounded
            onClick={() => setShowDialog(true)}
          >
            <i className="icon-generaledit" />
            Edit
          </StyledEditButton>
          <StyledDeleteButton
            type="button"
            rounded
            onClick={() => setShowConfirmDelete(true)}
          >
            <i className="icon-categorysanitarydisposal" />
            Delete
          </StyledDeleteButton>
        </Container>
        {responseMessage && (
          <Container
            backgroundColor="#eaefff"
            padding="20px"
            borderRadius="15px"
            fontSize="16px"
            marginTop="20px"
            marginBottom="15px"
          >
            <TextReveal
              lines={3}
              wordBreak="break-word"
              display="block"
              fontSize="16px"
              renderShowMore={({ onClick, show }) => (
                <EmptyButton
                  onClick={onClick}
                  display="flex"
                  alignItems="center"
                  marginTop="10px"
                  color="text300"
                  fontSize="16px"
                >
                  See {show ? 'Less' : 'More'}
                  <Icon
                    iconSize="25px"
                    marginBottom="-7px"
                    className={`ls-icon icon-generalcaret${
                      show ? 'up' : 'down'
                    }medium`}
                  />
                </EmptyButton>
              )}
            >
              {responseMessage}
            </TextReveal>
          </Container>
        )}
      </StyledRoot>
      <ReviewUpdate
        showDialog={showDialog}
        review={review}
        setShowDialog={setShowDialog}
        updateReviewCache={updateReviewCache}
      />
      <AlertDialog
        zIndex="999999"
        show={showConfirmDelete}
        heading="Are you sure you want to delete your review?"
        text="If you continue to delete, your review will be lost forever."
        buttons={[
          {
            children: 'No, don’t delete',
            props: {
              backgroundColor: 'white',
              rounded: true,
              onClick: () => setShowConfirmDelete(false),
            },
          },
          {
            children: 'Yes, delete',
            props: {
              primary: true,
              rounded: true,
              onClick: () => {
                setShowConfirmDelete(false);
                deleteReview(id);
              },
            },
          },
        ]}
      />
    </>
  );
};

export default Review;
